import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const StyledSwitch = styled.label(({ $disabled = false }) => ({
    width: "100%",
    margin: 0,
    padding: tokens.globals["spacing-3"],
    background: tokens.colors.surfaces["page-background"],
    borderRadius: tokens.globals["border-radius"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-2"],
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.6 : 1,
}));

const StyledRow = styled.span({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: tokens.globals["spacing-3"],
});

const StyledLabelText = styled.span({
    minWidth: 0,
    flex: 1,
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "16px",
});

const StyledDescription = styled.span({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "18px",
});

const StyledControl = styled.span({
    flexShrink: 0,
    position: "relative",
    display: "inline-flex",
    alignItems: "center",

    "input:focus-visible + span": {
        outline: `2px solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "2px",
    },
});

const StyledInput = styled.input({
    position: "absolute",
    width: tokens.globals["spacing-px"],
    height: tokens.globals["spacing-px"],
    margin: "-1px",
    padding: 0,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
});

const StyledTrack = styled.span(({ $checked = false }) => ({
    width: tokens.globals["spacing-8"],
    height: "18px",
    padding: tokens.globals["spacing-px"],
    borderRadius: "9999px",
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    background: $checked ? tokens.colors.theme["button-color"] : tokens.colors.neutral.grey["300"],
    transition: "background 0.2s ease-in-out",
}));

const StyledThumb = styled.span(({ $checked = false }) => ({
    width: tokens.globals["spacing-4"],
    height: tokens.globals["spacing-4"],
    borderRadius: "50%",
    background: tokens.colors.neutral.white,
    boxShadow: tokens.effects.effects.shadow.default,
    transform: $checked ? "translateX(14px)" : "translateX(0)",
    transition: "transform 0.2s ease-in-out",
}));

/**
 * Accessible AIE toggle switch. Renders a visually-hidden native `<input type="checkbox"
 * role="switch">` under the track/thumb; the whole card (label + description) is clickable.
 * @param {boolean} [props.checked] Checked (on) state.
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} [props.onChange] Change handler.
 * @param {React.Ref} ref Forwarded to the native input element.
 * Also: label, description, disabled, disabledReason, id, name, className (self-explanatory).
 */
const ToggleSwitch = forwardRef(function ToggleSwitch(
    {
        checked = false,
        onChange,
        label,
        description,
        disabled = false,
        disabledReason,
        id,
        name,
        className,
        ...props
    },
    ref
) {
    const inputId = id || name || undefined;
    const descriptionId = description && inputId ? `${inputId}-description` : undefined;

    return (
        <StyledSwitch
            htmlFor={inputId}
            $disabled={disabled}
            className={className}
            title={disabled ? disabledReason : undefined}
        >
            <StyledRow>
                {label && <StyledLabelText>{label}</StyledLabelText>}

                <StyledControl>
                    <StyledInput
                        {...props}
                        ref={ref}
                        id={inputId}
                        name={name}
                        type="checkbox"
                        role="switch"
                        checked={checked}
                        onChange={onChange}
                        disabled={disabled}
                        aria-checked={checked}
                        aria-label={!label ? name || "Toggle" : undefined}
                        aria-describedby={descriptionId}
                    />
                    <StyledTrack $checked={checked} aria-hidden="true">
                        <StyledThumb $checked={checked} />
                    </StyledTrack>
                </StyledControl>
            </StyledRow>

            {description && <StyledDescription id={descriptionId}>{description}</StyledDescription>}
        </StyledSwitch>
    );
});

ToggleSwitch.displayName = "ToggleSwitch";

export default ToggleSwitch;
